import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  LinearProgress,
} from '@mui/material';
import {
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  WorkspacePremium as AwardIcon,
} from '@mui/icons-material';

import ProfileService from '../services/ProfileService';
import JobSeekerProfileService from '../services/JobSeekerProfileService';
import CompanyProfileService from '../services/CompanyProfileService';
import UserTypeService from '../services/UserTypeService';
import { useAuth } from '../services/AuthService';
import { getValidationSchema, getStepValidationSchema, completeJobSeekerSchema, completeCompanySchema } from '../validation/onboardingSchema';
import { usePostHog } from '@posthog/react';
import { getSafeRedirectUrl } from '../utils/redirectUtils';

// Onboarding Components
import UserTypeStep from '../components/onboarding/UserTypeStep';
import BasicInfoStep from '../components/onboarding/BasicInfoStep';
import JobSeekerStep from '../components/onboarding/JobSeekerStep';
import CompanyStep from '../components/onboarding/CompanyStep';









const OnboardingProfileBuilder = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = getSafeRedirectUrl(searchParams.get('returnTo'), null) || null;
  const posthog = usePostHog();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [rateLimitError, setRateLimitError] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    defaultValues: {
      userType: '',
      profile: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        whatsapp: '',
      },
      jobseeker: {
        location: '',
        experience: '',
        availability: '',
        expectedSalary: '',
        skills: [],
        labourRequirements: [],
        visaStatus: '',
        transport: '',
        hasValidABN: '',
        abnNumber: '',
        countryOfOrigin: '',
        nativeLanguage: '',
        englishLevel: '',
        suburb: '',
        postcode: '',
        state: '',
        lat: null,
        lng: null,
      },
      company: {
        companyName: '',
        abn: '',
        industry: '',
        companySize: '',
        urgency: '',
        companyPhone: '',
        companyEmail: '',
        preferredCommunication: [],
      },
    },
  });

  const { reset, watch } = methods;
  const watchedUserType = watch('userType');
  const userType = watchedUserType || '';

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        if (!user) {
          //setIsLoading(false);
          return;
        }

        setIsLoading(true);
        setRateLimitError(false);
        const userId = user?.sub;
        let profile = null;
        
        try {
          profile = await new ProfileService().getRecordsByUserId(userId);
          
          // If profile is already completed, redirect immediately
          if (profile?.isCompleted) {
            const destination = returnTo && returnTo !== '/' ? returnTo : '/dashboard';
            navigate(destination, { replace: true });
            return;
          }
        } catch (error) {
          if (error?.status === 429) {
            setRateLimitError(true);
            return;
          }
          throw error;
        }

        try {
          const jobSeekerProfile = await new JobSeekerProfileService().getRecordsByUserId(userId);
          const companyProfile = await new CompanyProfileService().getRecordsByUserId(userId);

          const mergedData = {
            userType: profile?.userType || '',
            profile: {
              firstName: profile?.firstName || user?.given_name || '',
              lastName: profile?.lastName || user?.family_name || '',
              email: profile?.email || user?.email || '',
              phone: profile?.phone || user?.phone_number || '',
              whatsapp: profile?.whatsapp || '',
            },
            jobseeker: {
              location: jobSeekerProfile?.location || '',
              experience: jobSeekerProfile?.experience || '',
              availability: jobSeekerProfile?.availability || '',
              expectedSalary: jobSeekerProfile?.expectedSalary || '',
              skills: jobSeekerProfile?.skills || [],
              labourRequirements: jobSeekerProfile?.labourRequirements || [],
              visaStatus: jobSeekerProfile?.visaStatus || '',
              transport: jobSeekerProfile?.transport || '',
              hasValidABN: jobSeekerProfile?.hasValidABN || '',
              abnNumber: jobSeekerProfile?.abnNumber || '',
              countryOfOrigin: jobSeekerProfile?.countryOfOrigin || '',
              nativeLanguage: jobSeekerProfile?.nativeLanguage || '',
              englishLevel: jobSeekerProfile?.englishLevel || '',
              suburb: jobSeekerProfile?.suburb || '',
              postcode: jobSeekerProfile?.postcode || '',
              state: jobSeekerProfile?.state || '',
              lat: jobSeekerProfile?.lat ?? null,
              lng: jobSeekerProfile?.lng ?? null,
            },
            company: {
              companyName: companyProfile?.companyName || '',
              abn: companyProfile?.abn || '',
              industry: companyProfile?.industry || '',
              companySize: companyProfile?.companySize || '',
              urgency: companyProfile?.urgency || '',
              companyPhone: companyProfile?.companyPhone || '',
              companyEmail: companyProfile?.companyEmail || '',
              preferredCommunication: companyProfile?.preferredCommunication || [],
            },
          };

          reset(mergedData);
        } catch (error) {
          if (error?.status === 429) {
            setRateLimitError(true);
            return;
          }
          throw error;
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, [user, reset, navigate, returnTo]);

  const processAndSaveData = async (data) => {
    const userId = user?.sub;
    
    try {
      // Final validation before submission
      const finalSchema = getValidationSchema(data.userType);
      methods.setValue('isCompleted', true);
      await finalSchema.validate(data, { abortEarly: false });
      
      console.log('Submitting data:', { userId, ...data });

      // Data is already split into logical structures — no mapping needed
      await new ProfileService().createOrUpdateRecord({ userId, userType, ...data.profile, isCompleted: true });

      if (userType === 'jobseeker') {
        await new JobSeekerProfileService().createOrUpdateRecord({ userId, ...data.jobseeker });
        posthog?.capture('onboarding_completed', { user_type: 'jobseeker' });
      } else if (userType === 'company') {
        await new CompanyProfileService().createOrUpdateRecord({ userId, ...data.company });
        posthog?.capture('onboarding_completed', { user_type: 'company' });
      }

      // Navigate after all data is saved — go directly to /dashboard
      // Both user types resolve to /dashboard, no need for async getDashboardRoute()
      UserTypeService.clearCache(userId);
      const destination = returnTo && returnTo !== '/' ? returnTo : '/dashboard';
      navigate(destination, { replace: true });
    } catch (error) {
      console.error('Error creating profile:', error);
      if (error.name === 'ValidationError') {
        // Handle validation errors
        error.inner.forEach((err) => {
          methods.setError(err.path, {
            type: 'manual',
            message: err.message,
          });
        });
      }
      // Handle other errors (show toast, etc.)
    }
  };

  const steps = useMemo(() => [
    { component: UserTypeStep, title: 'User Type' },
    { component: BasicInfoStep, title: 'Basic Info' },
    ...(userType === 'jobseeker' ? [{ component: JobSeekerStep, title: 'Work Profile' }] : []),
    ...(userType === 'company' ? [{ component: CompanyStep, title: 'Company Profile' }] : []),
  ], [userType]);

  const handleProfileStep = async (data) => {
    await new ProfileService().createOrUpdateRecord({
      userId: user?.sub,
      ...data.profile
    });
  }

  const nextStep = async (e) => {
    e.preventDefault();
    
    // Get step-specific validation schema
    const stepSchema = getStepValidationSchema(currentStep, userType);
    
    try {
      // Get current form values
      const currentValues = methods.getValues();
      
      // Validate only the fields relevant to the current step
      await stepSchema.validate(currentValues, { abortEarly: false });
      
      if (steps[currentStep].title === 'Basic Info') {
        console.log('Basic Info step completed');
        handleProfileStep(currentValues);
      }
      // If validation passes, proceed to next step
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    } catch (validationError) {
      // Handle validation errors for current step only
      if (validationError.name === 'ValidationError') {
        // Clear previous errors for this step
        const stepFields = Object.keys(stepSchema.fields);
        stepFields.forEach(field => {
          methods.clearErrors(field);
        });
        
        // Set new errors
        validationError.inner.forEach((err) => {
          methods.setError(err.path, {
            type: 'manual',
            message: err.message,
          });
        });
        
        // Focus on first error field
        const firstErrorField = validationError.inner[0]?.path;
        if (firstErrorField) {
          methods.setFocus(firstErrorField);
        }
      }
      
      console.log('Step validation errors:', validationError.inner?.map(err => ({ field: err.path, message: err.message })));
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const CurrentStepComponent = steps[currentStep]?.component;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (currentStep === steps.length - 1) {
      // Use complete validation schema for final submission
      const completeSchema = userType === 'jobseeker' ? completeJobSeekerSchema : completeCompanySchema;
      
      try {
        const currentValues = methods.getValues();
        await completeSchema.validate(currentValues, { abortEarly: false });
        
        // If validation passes, proceed with form submission
        await processAndSaveData(currentValues);
      } catch (validationError) {
        // Handle validation errors for final submission
        if (validationError.name === 'ValidationError') {
          // Clear all previous errors
          methods.clearErrors();
          
          // Set new errors
          validationError.inner.forEach((err) => {
            methods.setError(err.path, {
              type: 'manual',
              message: err.message,
            });
          });
          
          // Focus on first error field
          const firstErrorField = validationError.inner[0]?.path;
          if (firstErrorField) {
            methods.setFocus(firstErrorField);
          }
        }
        
        console.log('Final submission validation errors:', validationError.inner?.map(err => ({ field: err.path, message: err.message })));
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  // Show loading screen while data is being fetched or user is not available
  if (isLoading || !user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : `linear-gradient(to bottom right, ${theme.palette.info.light}22, ${theme.palette.primary[50]})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Loading your profile...
        </Typography>
      </Box>
    );
  }

  // Show error if system is rate limited
  if (rateLimitError) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : `linear-gradient(to bottom right, ${theme.palette.info.light}22, ${theme.palette.primary[50]})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Container maxWidth="sm">
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            <AlertTitle sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 1 }}>
              System Temporarily Unavailable
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              We're experiencing high demand at the moment. Our system is temporarily unavailable. Please try again in a few moments.
            </Typography>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => window.location.reload()}
              sx={{ mt: 1 }}
            >
              Refresh Page
            </Button>
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleFormSubmit}
        onKeyDown={handleKeyDown}
        sx={{
          minHeight: '100vh',
          background: (theme) => theme.palette.mode === 'dark' ? theme.palette.background.paper : `linear-gradient(to bottom right, ${theme.palette.info.light}22, ${theme.palette.primary[50]})`,
          py: 4,
          px: 2,
        }}
      >
        <Container maxWidth="lg">
          {userType && (
            <Box mb={4}>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Step {currentStep + 1} of {steps.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={((currentStep + 1) / steps.length) * 100}
                color="primary"
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          <Card elevation={4}>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
              {CurrentStepComponent && <CurrentStepComponent />}

              <Stack
                direction="row"
                justifyContent="space-between"
                mt={4}
                pt={3}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Button
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  variant="text"
                  startIcon={<ChevronLeftIcon />}
                >
                  Back
                </Button>

                {currentStep === steps.length - 1 ? (
                   <Button
                     type="submit"
                     disabled={!watchedUserType}
                     variant="contained"
                     sx={{
                       background: (theme) => `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                       '&:hover': {
                         background: (theme) => `linear-gradient(to right, ${theme.palette.info.dark}, ${theme.palette.primary.main})`,
                       },
                     }}
                     endIcon={<AwardIcon />}
                   >
                     Complete Setup
                   </Button>
                 ) : (
                   <Button
                     onClick={nextStep}
                     disabled={!watchedUserType}
                     variant="contained"
                     sx={{
                       background: (theme) => `linear-gradient(to right, ${theme.palette.info.main}, ${theme.palette.primary.main})`,
                       '&:hover': {
                         background: (theme) => `linear-gradient(to right, ${theme.palette.info.dark}, ${theme.palette.primary.main})`,
                       },
                     }}
                     endIcon={<ChevronRightIcon />}
                   >
                     Continue
                   </Button>
                 )}
              </Stack>
            </CardContent>
          </Card>

          <Typography align="center" mt={3} variant="body2" color="text.secondary">
            Join thousands of workers and employers already connected on our platform
          </Typography>
        </Container>
      </Box>
    </FormProvider>
  );
};

export default OnboardingProfileBuilder;
